<script setup lang="ts">
import { computed } from 'vue'

import type { PlayerSnapshot } from '@/composables/usePlayer'
import type { RuntimeConfig } from '@/config'
import { recentLogs } from '@/lib/log'
import { formatAspectRatio, type StageBox } from '@/lib/stage'
import type { Playlist } from '@/types'

const props = defineProps<{
  config: RuntimeConfig
  playlist: Playlist | null
  snapshot: PlayerSnapshot | null
  playlistError: string | null
  lastLoadedAt: number
  stageBox: StageBox | null
  /** Bumped by the parent to force a refresh of the log list. */
  tick: number
}>()

const buildId = __BUILD_ID__

/** A slug that does not exist falls back to `default`; make that obvious. */
const playlistMismatch = computed(
  () => props.playlist !== null && props.playlist.name !== props.config.playlistName,
)

const stageDescription = computed(() => {
  const box = props.stageBox
  const aspect = formatAspectRatio(props.config.aspect)
  if (!box) return aspect
  const bars = box.letterboxed ? ` · letterboxed (viewport is not ${aspect})` : ''
  return `${aspect} · ${box.width}×${box.height}${bars}`
})

const logs = computed(() => {
  void props.tick
  return recentLogs(8)
})

function seconds(ms: number): string {
  return `${(ms / 1_000).toFixed(1)}s`
}

function clock(at: number): string {
  return at === 0 ? 'never' : new Date(at).toLocaleTimeString('nb-NO')
}
</script>

<template>
  <div class="debug">
    <div class="row heading">
      <strong>Bildusjen infoscreens</strong>
      <span>build {{ buildId }}</span>
    </div>

    <div class="row" :class="{ warn: playlistMismatch }">
      <span>playlist</span>
      <span>
        {{ playlist?.name ?? '—' }} @ {{ playlist?.revision ?? '—' }} · asked for
        {{ config.playlistName }} (from {{ config.playlistSource }})
      </span>
    </div>
    <div class="row">
      <span>manifest</span>
      <span>
        loaded {{ clock(lastLoadedAt) }}
        <template v-if="playlistError"> · error: {{ playlistError }}</template>
      </span>
    </div>
    <div class="row">
      <span>items</span>
      <span>
        {{ snapshot?.scheduledCount ?? 0 }}/{{ snapshot?.itemCount ?? 0 }} playable · cycle
        {{ seconds(snapshot?.cycleMs ?? 0) }}
      </span>
    </div>
    <div class="row">
      <span>showing</span>
      <span>
        #{{ snapshot?.currentIndex ?? '—' }} {{ snapshot?.currentItemId ?? '—' }} (expected #{{
          snapshot?.expectedIndex ?? '—'
        }})
      </span>
    </div>
    <div class="row" :class="{ warn: stageBox?.letterboxed }">
      <span>stage</span>
      <span>{{ stageDescription }}</span>
    </div>
    <div class="row">
      <span>mode</span>
      <span>
        sync={{ config.sync }} · fade={{ snapshot?.transitionMs ?? 0 }}ms · sound={{ config.sound }}
        · recoveries={{ snapshot?.recoveries ?? 0 }}
      </span>
    </div>
    <div v-if="snapshot?.failedIds.length" class="row warn">
      <span>dropped</span>
      <span>{{ snapshot.failedIds.join(', ') }}</span>
    </div>

    <div v-for="(layer, index) in snapshot?.layers ?? []" :key="index" class="row">
      <span>layer {{ index }}{{ snapshot?.activeSlot === index ? ' *' : '' }}</span>
      <span v-if="layer">
        {{ layer.kind ?? 'empty' }} {{ layer.itemId ?? '' }} · ready={{ layer.ready }} ·
        {{ seconds(layer.currentTimeMs) }}/{{ seconds(layer.durationMs) }} · paused={{
          layer.paused
        }}
        <template v-if="layer.error"> · {{ layer.error }}</template>
      </span>
      <span v-else>not mounted</span>
    </div>

    <div class="logs">
      <div v-for="entry in logs" :key="`${entry.at}-${entry.message}`" :class="['log', entry.level]">
        {{ clock(entry.at) }} {{ entry.message }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.debug {
  position: absolute;
  inset: auto auto 0 0;
  z-index: 10;
  max-width: min(720px, 100vw);
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.72);
  color: #d6f5ff;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.45;
  pointer-events: none;
}

.row {
  display: flex;
  gap: 10px;
}

.row > span:first-child {
  flex: 0 0 76px;
  color: #7bb6c7;
}

.heading {
  margin-bottom: 4px;
  justify-content: space-between;
  color: #fff;
}

.warn > span {
  color: #ffcf6b;
}

.logs {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
}

.log.warn {
  color: #ffcf6b;
}

.log.error {
  color: #ff9b8a;
}
</style>
